import React, { useState, useEffect } from 'react';
import { API_BASE_URL, CONNECT_UI_URL, CONNECT_CLIENT_ID } from '../../config.js';
import SortableTable from '../SortableTable.jsx';
import { _t } from '../../utils/i18n.js';

const UsersManagement = ({ fetchWithAuth }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch users data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch users
        const usersResponse = await fetchWithAuth(`${API_BASE_URL}/api/admin/users`);
        if (!usersResponse.ok) throw new Error('Failed to fetch users');
        const usersData = await usersResponse.json();

        setUsers(usersData);
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fetchWithAuth]);

  // Define columns for SortableTable
  const columns = [
    {
      key: 'username',
      label: 'Nom d\'utilisateur',
      render: (user) => (
        <span className="font-medium text-gray-900 dark:text-white">
          {user.username}
        </span>
      )
    },
    {
      key: 'client',
      label: 'Client',
      render: (user) => (
        <span className="text-gray-500 dark:text-gray-300">
          {user.client || '-'}
        </span>
      )
    },
    {
      key: 'authorization',
      label: 'Autorisation',
      filterable: true,
      sortFunction: (a, b, direction) => {
        // Define priority order: Super Admin (3), Admin (2), Standard (1)
        const getPriority = (user) => {
          if (user.issuperadmin) return 3;
          if (user.isadmin) return 2;
          return 1;
        };

        const aPriority = getPriority(a);
        const bPriority = getPriority(b);

        if (direction === 'ascending') {
          return bPriority - aPriority; // Higher priority first
        } else {
          return aPriority - bPriority; // Lower priority first
        }
      },
      rawValue: (user) => {
        if (user.issuperadmin) return 'Super Admin';
        if (user.isadmin) return 'Admin';
        return 'Standard';
      },
      render: (user) => (
        <div className="flex flex-col gap-1">
          {user.issuperadmin && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 w-fit">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Super Admin
            </span>
          )}
          {user.isadmin && !user.issuperadmin && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 w-fit">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Admin
            </span>
          )}
          {!user.isadmin && !user.issuperadmin && (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 w-fit">
              Standard
            </span>
          )}
        </div>
      )
    },
            {
      key: 'modules',
      label: 'Modules',
      filterable: true,
      rawValue: (user) => {
        // Create a searchable string that includes all modules for this user
        if (user.bi_modules?.allowed?.length > 0) {
          // Return each module as a separate searchable value
          return user.bi_modules.allowed;
        }

        if (user.issuperadmin) {
          return ['Tous les modules'];
        }

        return ['Aucun module'];
      },
      render: (user) => (
        <div className="flex flex-wrap gap-1 max-w-xs">
          {user.bi_modules?.allowed?.length > 0 ? (
            user.bi_modules.allowed.map(moduleId => (
              <span
                key={moduleId}
                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                title={moduleId}
              >
                {moduleId}
              </span>
            ))
          ) : user.issuperadmin ? (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
              title="Tous les modules"
            >
              Tous les modules
            </span>
          ) : (
            <span className="text-gray-400 italic text-xs">Aucun module</span>
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden h-full flex flex-col">
      <div className="p-6 flex-shrink-0">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Gestion des utilisateurs</h2>

        <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
            {_t('Users and access rights are managed in Globe Connect.')}
          </p>
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
            {_t('This page is read-only. A change made here would have no effect: Connect is the only writer.')}
          </p>
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
            {_t('The list below is blurred on purpose: Insights can lag behind Connect, the only source of truth for roles and access.')}
          </p>
          <a
            href={`${CONNECT_UI_URL}/admin/droits?app=${CONNECT_CLIENT_ID}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 border border-blue-300 dark:border-blue-700 rounded-md shadow-sm text-sm font-medium text-blue-700 dark:text-blue-200 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
          >
            {_t('Manage users in Connect')}
          </a>
        </div>
      </div>

      {/* Blurred on purpose: the list stays as a hint that data exists, unreadable so nobody
          deduces a role here. Inert and aria-hidden — a filter or a sort would hand back exactly
          what the blur removes. `grayscale` with it: a blur keeps the badge colours, and the green
          of Super Admin names the role on its own. */}
      <div className="px-6 flex-1 overflow-hidden blur-[7px] grayscale select-none pointer-events-none" aria-hidden="true">
        <SortableTable
          data={users}
          columns={columns}
          emptyMessage="Aucun utilisateur trouvé"
          countLabel={`Total: ${users.length} utilisateur${users.length > 1 ? 's' : ''}`}
          initialSortBy={{ key: 'username', direction: 'ascending' }}
          fullHeight={true}
        />
      </div>
    </div>
  );
};

export default UsersManagement;